"use client";

import Image from "next/image";
import React, { useCallback } from "react";

interface CarouselItemProps {
  readonly height: number;
  readonly width: number;
  readonly shape?: string;
  readonly image: string;
  readonly video?: string;
  readonly imageAlt: string;
  readonly priority?: boolean;
}

// eslint-disable-next-line max-lines-per-function -- barely over threshold (53 lines)
const CarouselItem: React.FC<CarouselItemProps> = ({
  height = 512,
  width = 512,
  shape = "default",
  image,
  video,
  imageAlt = "",
  priority = false,
}) => {
  const handleMetadataLoaded = useCallback(
    (event: React.SyntheticEvent<HTMLVideoElement, Event>): void => {
      // eslint-disable-next-line functional/immutable-data -- DOM video API
      event.currentTarget.currentTime = 0;
    },
    [],
  );

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLVideoElement>): void => {
    void e.currentTarget.play();
  }, []);

  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLVideoElement>): void => {
    e.currentTarget.pause();
  }, []);

  if (video) {
    return (
      <video
        className={`carousel-video ${shape}`}
        data-shape={shape}
        height={height}
        poster={image}
        preload="auto"
        width={width}
        loop
        muted
        playsInline
        onLoadedMetadata={handleMetadataLoaded}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <source src={video} type="video/mp4" />
      </video>
    );
  }

  return (
    <Image
      alt={imageAlt}
      className={`carousel-image ${shape}`}
      data-shape={shape}
      height={height}
      priority={priority}
      src={image}
      width={width}
    />
  );
};

export default React.memo(CarouselItem);
